import React from "react";
import {useFormContext} from "react-hook-form";
import {Box, Colors, Text} from "@bounce/shared/ui";
import ChipsSelectInput from "./ChipsSelectInput";
import {HOURS, PERMITTED_HOURS_HELP, WEEKDAYS} from "./constants";
import {IconCalendarPlus, IconClock} from "../../../../assets";
import {formatWeekday} from "../helpers";
import {PolicyBlock, ToggleChipButton} from "./PolicyBlock";
import {FormValues} from "./schema";
import {usePermittedHours} from "./usePermittedHours";

const HOUR_BUTTON_MIN_WIDTH = 36;
const WEEKDAY_BUTTON_MIN_WIDTH = 48;
const WEEKDAY_HOURS_HELP = "Added on top of the schedule on these weekdays.";
const NO_SKIPPED_DAYS_TEXT = "None";

const toggle = <T,>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((current) => current !== value) : [...values, value];

export const PermittedHoursBlock = ({variantIndex}: {variantIndex: number}) => {
  const {watch, setValue} = useFormContext<FormValues>();
  const path = `variants.${variantIndex}.policy.permitted_hours` as const;
  const permittedHours = watch(path);

  return (
    <PolicyBlock title="Permitted hours" icon={IconClock} help={PERMITTED_HOURS_HELP}>
      <Box role="group" aria-label="Permitted hours" sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
        {HOURS.map((hour) => (
          <ToggleChipButton
            key={hour}
            selected={permittedHours.includes(hour)}
            onClick={() => setValue(path, toggle(permittedHours, hour), {shouldDirty: true})}
            minWidth={HOUR_BUTTON_MIN_WIDTH}
          >
            {hour}
          </ToggleChipButton>
        ))}
      </Box>
    </PolicyBlock>
  );
};

export const WeekdayBlock = ({variantIndex}: {variantIndex: number}) => {
  const {watch, setValue} = useFormContext<FormValues>();
  const policyPath = `variants.${variantIndex}.policy` as const;
  const weekdayHours = watch(`${policyPath}.weekday_hours`);
  const skipWeekdays = watch(`${policyPath}.skip_weekdays`);
  const {isNotPermitted} = usePermittedHours(variantIndex);

  return (
    <PolicyBlock title="Extra hours by weekday" icon={IconCalendarPlus} help={WEEKDAY_HOURS_HELP}>
      <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1}}>
        {WEEKDAYS.map((weekday) => (
          <Box key={weekday} sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
            <Text size={12} sx={{color: Colors.grey._700}}>
              {formatWeekday(weekday)}
            </Text>
            <ChipsSelectInput
              value={weekdayHours[weekday]}
              options={HOURS}
              isInvalid={isNotPermitted}
              onChange={(next) => setValue(`${policyPath}.weekday_hours.${weekday}`, next, {shouldDirty: true})}
              placeholder="+ hour"
              ariaLabel={`Extra hours on ${formatWeekday(weekday)}`}
            />
          </Box>
        ))}
      </Box>
      <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
        <Text size={13} variant="inter600">
          Never retry on
        </Text>
        <Box role="group" aria-label="Skip weekdays" sx={{display: "flex", gap: 0.5}}>
          {WEEKDAYS.map((weekday) => (
            <ToggleChipButton
              key={weekday}
              selected={skipWeekdays.includes(weekday)}
              onClick={() =>
                setValue(`${policyPath}.skip_weekdays`, toggle(skipWeekdays, weekday), {shouldDirty: true})
              }
              minWidth={WEEKDAY_BUTTON_MIN_WIDTH}
            >
              {formatWeekday(weekday)}
            </ToggleChipButton>
          ))}
        </Box>
        {skipWeekdays.length === 0 && (
          <Text size={12} sx={{color: Colors.grey._600}}>
            {NO_SKIPPED_DAYS_TEXT}
          </Text>
        )}
      </Box>
    </PolicyBlock>
  );
};
